<script setup lang="ts">
import { Menu, MenuButton, MenuItem, MenuItems } from "@headlessui/vue";
import { computed, ref, watch } from "vue";
import { useRoute } from "vue-router";

import { useSidebarBackground } from "@/common/composables/useSidebarBackground";

import BaseIcon, { type BaseIconProps } from "../icons/BaseIcon.vue";

const props = withDefaults(
  defineProps<{
    title: string;
    options: { name: string; link?: string; value?: string }[];
    // `dropdownType` determines if the items in the dropdown will go inside its container or not
    icon?: BaseIconProps["name"];
    dropdownType?: "static" | "floating";
    // depending on the `optionType`, we show a link or a button. if it is a button, we emit an event when it is clicked
    optionType?: "link" | "button";
    /** this prop is used to distinguish styling from the usage of this component in the header vs. the sidebar */
    theme?: "light" | "dark";
    width?: "auto" | "full";
    closeOnClick?: boolean;
  }>(),
  {
    icon: undefined,
    dropdownType: "static",
    optionType: "link",
    theme: "dark",
    width: "full",
  },
);

defineEmits<{
  (e: "item-click", value: string | undefined, name: string): void;
}>();

const {
  sidebarBackgroundClass,
  sidebarHoverActiveClass,
  sidebarActiveClass,
  sidebarMenuItemClass,
} = useSidebarBackground();

// if `dropdownType` is static, we need to keep track of whether the dropdown is open or not manually
// otherwise we use the `open` value that the `Menu` component provides
const isDropdownOpen = ref(false);

const toggleDropdown = () => {
  isDropdownOpen.value = !isDropdownOpen.value;
};

const route = useRoute();
watch(
  route,
  () => {
    if (props.options.some((opt) => opt.link === route.path)) {
      isDropdownOpen.value = true;
    }
  },
  { immediate: true },
);

const themeStyles = computed(() => {
  if (props.theme === "dark") {
    return `${sidebarBackgroundClass.value} text-white`;
  }

  return "bg-slate-100 text-slate-700";
});

const menuItemsBackgroundClass = computed(() => {
  return sidebarMenuItemClass.value;
});

const menuItemHoverClass = computed(() => {
  return sidebarHoverActiveClass.value;
});

const menuItemActiveClass = computed(() => {
  return `${sidebarActiveClass.value} border-r-4 border-white`;
});

const widthClass = computed(() => {
  return `w-${props.width}`;
});
</script>

<template>
  <Menu
    v-slot="{ open, close }"
    as="div"
    class="relative inline-block text-left"
    :class="[themeStyles, widthClass]"
  >
    <div>
      <MenuButton
        class="flex justify-start items-center w-full py-2 px-4 text-sm"
        :class="menuItemHoverClass"
        @click="toggleDropdown()"
      >
        <div class="flex flex-1 items-center gap-x-4">
          <BaseIcon
            v-if="icon"
            :name="icon"
            class="size-6 text-white"
          />
          {{ title }}
        </div>

        <BaseIcon
          :name="
            (dropdownType === 'static' ? isDropdownOpen : open)
              ? 'heroicons:chevron-up'
              : 'heroicons:chevron-down'
          "
          class="-mr-1 ml-1 size-5"
          aria-hidden="true"
        />
      </MenuButton>
    </div>

    <Transition
      enter-active-class="transition ease-out duration-100"
      enter-from-class="transform opacity-0 scale-95"
      enter-to-class="transform opacity-100 scale-100"
      leave-active-class="transition ease-in duration-75"
      leave-from-class="transform opacity-100 scale-100"
      leave-to-class="transform opacity-0 scale-95"
    >
      <MenuItems
        v-show="dropdownType === 'static' ? isDropdownOpen : open"
        :static="dropdownType === 'static'"
        class="origin-top-right"
        :class="[
          widthClass,
          menuItemsBackgroundClass,
          dropdownType === 'floating'
            ? `absolute right-0  z-10`
            : 'relative w-full',
        ]"
      >
        <div
          class="max-h-[80vh] overflow-auto"
          @click="
            () => {
              closeOnClick && close();
              $emit('item-click', undefined, 'close');
            }
          "
        >
          <MenuItem
            v-for="navigation in options"
            :key="navigation.name"
            :data-cy="`vue-dropdown-menu-item-${navigation.name
              .toLowerCase()
              .replace(' ', '-')}`"
          >
            <RouterLink
              v-if="optionType === 'link' && navigation.link"
              :to="navigation.link"
              class="flex items-center p-2 pl-10 text-sm justify-between group text-white"
              :class="menuItemHoverClass"
              :active-class="menuItemActiveClass"
            >
              {{ navigation.name }}
            </RouterLink>
            <button
              v-else-if="optionType === 'button'"
              class="flex items-center p-2 text-sm justify-between group w-full text-white"
              :class="menuItemHoverClass"
              @click="$emit('item-click', navigation.value, navigation.name)"
            >
              {{ navigation.name }}
            </button>
          </MenuItem>
        </div>
      </MenuItems>
    </Transition>
  </Menu>
</template>
